import ModelExecutionResults from 'app/common_components/ModelExecutionResults/ModelExecutionResults';
import { ExecutionResults, Model, NamedModel, PostExecutionResults } from 'api/Exec/types';
import DockerImages from 'components/Dashboard/Models/DockerImages/DockerImages';
import { useAddNamedModel, useCheckNamedModelName, useEditNamedModel } from 'components/Dashboard/Models/NamedModels/NamedModelForm/hooks';

import React, { useState } from 'react';
import { Button, Checkbox, Flex, Form, Input, Space, Typography } from 'antd';

export interface FormValues {
  modelName: string;
  modelId: string;
  executionResults: ExecutionResults;
  postExecutionResults: PostExecutionResults;
  comment: string;
}

interface Props {
  onCancel: () => void;
  onNamedModelAdded: (namedModel: NamedModel) => void;
  onNamedModelUpdated: (namedModel: NamedModel) => void;
  namedModel: NamedModel | null;
}

const NamedModelForm: React.FC<Props> = ({ onCancel, onNamedModelAdded, onNamedModelUpdated, namedModel }) => {
  const [ form ] = Form.useForm<FormValues>();
  const { addNamedModel, isCreating } = useAddNamedModel(onNamedModelAdded);
  const { editNamedModel, isUpdating } = useEditNamedModel(onNamedModelUpdated);
  const { checkNamedModelName, isChecking } = useCheckNamedModelName();

  const [ selectedModel, setSelectedModel ] = useState<Model | null>(null);

  const isCv = !!namedModel && namedModel.clinicalValidated;

  const onFinish = (values: FormValues) => {
    if (namedModel) {
      editNamedModel(namedModel.id, values);
    } else {
      addNamedModel(values);
    }
  };

  const selectRow = (model: Model) => {
    setSelectedModel(model);
    form.setFieldValue('modelId', model.id);
    if (!namedModel) {
      form.setFieldValue('executionResults', model.executionResults);
      form.setFieldValue('postExecutionResults', model.postExecutionResults);
    } else {
      form.resetFields([ 'executionResults', 'postExecutionResults' ]);
    }
  };

  const testName = () => {
    const name = form.getFieldValue('modelName');

    if (name) {
      checkNamedModelName(name);
    }
  };

  return (
    <Form
      form={form}
      name="namedModel"
      layout="vertical"
      onFinish={onFinish}
      disabled={isCreating || isUpdating}
      initialValues={{
        modelName: namedModel ? namedModel.modelName : '',
        modelId: namedModel ? namedModel.modelId : '',
        executionResults: {
          resultSubPop: namedModel ? !!namedModel.executionResults?.resultSubPop : false,
          resultSubPopChrm: namedModel ? !!namedModel.executionResults?.resultSubPopChrm : false,
          resultSuperPop: namedModel ? !!namedModel.executionResults?.resultSuperPop : false,
          resultSuperPopChrm: namedModel ? !!namedModel.executionResults?.resultSuperPopChrm : false,
          resultSvgData: namedModel ? !!namedModel.executionResults?.resultSvgData : false,
          resultPrsData: namedModel ? !!namedModel.executionResults?.resultPrsData : false,
          resultPrsTechData: namedModel ? !!namedModel.executionResults?.resultPrsTechData : false,
          resultPrsQc: namedModel ? !!namedModel.executionResults?.resultPrsQc : false,
        },
        postExecutionResults: {
          ancestryPdfReport: namedModel ? !!namedModel.postExecutionResults?.ancestryPdfReport : false,
          prsRuoCardioPdfReport: namedModel ? !!namedModel.postExecutionResults?.prsRuoCardioPdfReport : false,
          prsRuoCancerPdfReport: namedModel ? !!namedModel.postExecutionResults?.prsRuoCancerPdfReport : false,
          prsClinicalCardioPdfReport: namedModel ? !!namedModel.postExecutionResults?.prsClinicalCardioPdfReport : false,
          prsClinicalCancerPdfReport: namedModel ? !!namedModel.postExecutionResults?.prsClinicalCancerPdfReport : false,
        },
        clinicalValidated: namedModel ? namedModel.clinicalValidated : false,
        enabled: namedModel ? namedModel.enabled : true,
        comment: namedModel?.comment || '',
      }}
    >
      <Flex align="center" gap={24}>
        <Form.Item
          style={{ width: 360 }}
          name="modelName"
          label="Named model name"
          rules={[ { required: true, message: 'Field is mandatory' } ]}
        >
          <Input disabled={isCv} placeholder="Enter named model name" />
        </Form.Item>

        <Button loading={isChecking} onClick={testName}>Check name</Button>
      </Flex>

      <Typography.Title level={5}>Docker image</Typography.Title>

      <DockerImages addingNamedModel selectRow={selectRow} selectedNamedModel={namedModel || undefined} />

      <Form.Item
        style={{ width: 360, marginTop: -50, marginBottom: 0 }}
        name="modelId"
        label="Selected docker image id"
        rules={[ { required: true, message: 'Select docker image from the table' } ]}
      >
        <Input disabled placeholder="Selected docker image id" />
      </Form.Item>

      <Form.Item name="enabled" valuePropName="checked" style={{ marginBottom: 0 }}>
        <Checkbox disabled={!namedModel}>Enable named model</Checkbox>
      </Form.Item>

      <Form.Item name="clinicalValidated" valuePropName="checked" style={{ marginBottom: 0 }}>
        <Checkbox disabled={!namedModel || isCv}>Clinical validated</Checkbox>
      </Form.Item>

      <Form.Item name="comment" label="Comment" style={{ width: 360 }}>
        <Input.TextArea placeholder="Enter сomment" />
      </Form.Item>

      <ModelExecutionResults
        executionResults={selectedModel?.executionResults || null}
        postExecutionResults={selectedModel?.postExecutionResults || null}
        viewMode={(!selectedModel && !namedModel) || isCv}
      />

      <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
        <Space>
          <Button onClick={onCancel}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit">
            {namedModel ? 'Edit' : 'Add'} named model
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

export default NamedModelForm;
